import { useCallback, useEffect, useState } from "react";
import { demoApi, problemMessage } from "@/api/client";
import type { CreatePlaceRequest, Guid, Place, UpdatePlaceRequest } from "@/api/types";

export type PlacesStatus = "loading" | "ready" | "error";

export interface UsePlacesResult {
  /** `null` until resolved and on failure. Active places only — never includes an
   *  archived row, which lives in `archived` below. */
  active: Place[] | null;
  /** Always `[]` rather than `null`: the archived disclosure has a real, always-valid
   *  answer ("nothing archived yet") even while `active` is still loading or failed. */
  archived: Place[];
  status: PlacesStatus;
  error: string | null;
  reload: () => Promise<void>;
  /** Throws on failure — Places.tsx catches this itself to read per-field validation
   *  errors off it (`problemFieldErrors`), which this hook has no opinion about. */
  create: (body: CreatePlaceRequest) => Promise<Place>;
  update: (id: Guid, body: UpdatePlaceRequest) => Promise<Place>;
  archive: (id: Guid) => Promise<void>;
  /** ef-038-3 — brings an archived place back. Idempotent server-side (a second call, or
   *  one against an already-active place, still succeeds), so this never needs a
   *  "was it already active" check before calling it. */
  restore: (id: Guid) => Promise<Place>;
}

/**
 * The farm's own saved locations (ef-021) — one GET, split into active/archived on the
 * client rather than two round trips, so opening the archived disclosure is instant.
 *
 * Owner-only server-side (403 for an employee login). This hook does not check that and
 * must not: a client-side role check is a rendering decision, and treating it as the
 * control is how the real one stops being maintained — same reasoning `usePendingTime`
 * gives. The Places screen this feeds is already behind the owner shell.
 *
 * Re-reads the WHOLE list after every write rather than patching the array in place. The
 * list is small (a farm's own fields, yards and buildings, not a growing transactional
 * table) and a re-read is the one implementation that can never drift from what the
 * server actually holds — e.g. after an archive, whether the punch-location resolution
 * downstream would consider a place gone.
 */
export function usePlaces(): UsePlacesResult {
  const [places, setPlaces] = useState<Place[] | null>(null);
  const [status, setStatus] = useState<PlacesStatus>("loading");
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (signal?: AbortSignal): Promise<void> => {
    try {
      const resolved = await demoApi.getPlaces(true, signal);
      if (signal?.aborted) return;
      setPlaces(resolved);
      setStatus("ready");
      setError(null);
    } catch (err) {
      if (signal?.aborted || (err instanceof DOMException && err.name === "AbortError")) return;
      setPlaces(null);
      setStatus("error");
      setError(problemMessage(err, "Could not load the farm's saved places."));
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const reload = useCallback(() => load(), [load]);

  const create = useCallback(
    async (body: CreatePlaceRequest): Promise<Place> => {
      const place = await demoApi.createPlace(body);
      await load();
      return place;
    },
    [load],
  );

  const update = useCallback(
    async (id: Guid, body: UpdatePlaceRequest): Promise<Place> => {
      const place = await demoApi.updatePlace(id, body);
      await load();
      return place;
    },
    [load],
  );

  const archive = useCallback(
    async (id: Guid): Promise<void> => {
      await demoApi.archivePlace(id);
      await load();
    },
    [load],
  );

  const restore = useCallback(
    async (id: Guid): Promise<Place> => {
      const place = await demoApi.unarchivePlace(id);
      await load();
      return place;
    },
    [load],
  );

  const active = places === null ? null : places.filter((p) => p.archivedAt === null);
  const archived = places === null ? [] : places.filter((p) => p.archivedAt !== null);

  return { active, archived, status, error, reload, create, update, archive, restore };
}
